<template>
  <div class="px-3 px-md-4 px-lg-15 py-4 manual-survey">
    <div class="d-flex align-items-center mb-4">
      <h2 class="size-20 fw-600 text-dark mb-0">En tienda física</h2>
    </div>

    <!-- Paso 1: Competidor -->
    <section class="mb-4">
      <CompetitorSelector
        :competitors="competitors"
        :selected-competitor="selectedCompetitor"
        :loading="loadingCompetitors"
        @select="onSelectCompetitor"
      />
    </section>

    <!-- Paso 2: Producto -->
    <section
      class="mb-4"
      :class="{ 'opacity-50 pointer-events-none': !selectedCompetitor }"
    >
      <ProductSearch
        :selected-product="selectedProduct"
        :product-results="productResults"
        :loading="loadingProducts"
        @search="onSearchProducts"
        @select="onSelectProduct"
        @clear="onClearProduct"
        @clearResults="onClearResults"
      />
    </section>

    <!-- Paso 3: Formulario -->
    <section
      :class="{ 'opacity-50 pointer-events-none': !selectedProduct }"
    >
      <SurveyForm
        v-model="form"
        :loading="loadingSave"
        :errors="errors"
        @save="onSave"
        @clear="onClearForm"
      />
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CompetitorSelector from './CompetitorSelector.vue'
import ProductSearch from './ProductSearch.vue'
import SurveyForm from './SurveyForm.vue'

export default {
  name: 'manual-survey',
  components: {
    CompetitorSelector,
    ProductSearch,
    SurveyForm
  },
  computed: {
    ...mapGetters(['authentication_user'])
  },
  data() {
    return {
      competitors: [],
      loadingCompetitors: false,
      selectedCompetitor: null,

      productResults: [],
      loadingProducts: false,
      selectedProduct: null,

      form: {
        competitor_sku: '',
        competitor_product_name: '',
        competitor_original_price: null,
        competitor_sale_price: null,
        competitor_stock: false
      },
      loadingSave: false,
      errors: {}
    }
  },
  created() {
    this.fetchCompetitors()
  },
  methods: {
    ...mapActions(['get', 'store']),

    fetchCompetitors() {
      this.loadingCompetitors = true
      let url = '/api/pricescrapingcompetitors/'
      if (!this.authentication_user.client_channel) {
        url = url + `?business=${this.authentication_user.client_business_select}`
      }
      this.get({ url }).then((response) => {
        this.competitors = response.results || []
        this.loadingCompetitors = false
      }).catch(() => {
        this.loadingCompetitors = false
        this.$notification.error(
          'Error al cargar competidores.',
          { timer: 5, showCloseIcn: true }
        )
      })
    },

    onSelectCompetitor(competitor) {
      this.selectedCompetitor = competitor
      this.onClearProduct()
    },

    onSearchProducts({ query, searchType }) {
      this.loadingProducts = true
      let url = '/api/products/'
      const params = {
        search: query,
        search_type: searchType,
        page_size: 20
      }
      if (!this.authentication_user.client_channel) {
        params.business = this.authentication_user.client_business_select
      }
      this.get({ url, params }).then((response) => {
        this.productResults = response.results || []
        this.loadingProducts = false
      }).catch(() => {
        this.loadingProducts = false
        this.productResults = []
      })
    },

    onClearResults() {
      this.productResults = []
    },

    onSelectProduct(product) {
      this.selectedProduct = product
      // Pre-llenar nombre con el nombre propio como sugerencia
      this.form.competitor_product_name = product.long_name || ''
    },

    onClearProduct() {
      this.selectedProduct = null
      this.productResults = []
      this.onClearForm()
    },

    onClearForm() {
      this.form = {
        competitor_sku: '',
        competitor_product_name: '',
        competitor_original_price: null,
        competitor_sale_price: null,
        competitor_stock: false
      }
      this.errors = {}
    },

    validateForm() {
      const errors = {}
      if (!this.form.competitor_product_name || this.form.competitor_product_name.trim().length < 2) {
        errors.competitor_product_name = 'El nombre del producto es obligatorio (mínimo 2 caracteres).'
      }
      if (this.form.competitor_original_price === null || this.form.competitor_original_price === '' || this.form.competitor_original_price <= 0) {
        errors.competitor_original_price = 'El precio lista es obligatorio y debe ser mayor a 0.'
      }
      if (this.form.competitor_sale_price === null || this.form.competitor_sale_price === '' || this.form.competitor_sale_price < 0) {
        errors.competitor_sale_price = 'El precio descuento es obligatorio y debe ser mayor o igual a 0.'
      }
      this.errors = errors
      return Object.keys(errors).length === 0
    },

    onSave() {
      if (!this.validateForm()) return

      this.loadingSave = true
      const payload = {
        client_competitor: this.selectedCompetitor.id,
        product: this.selectedProduct.id,
        competitor_sku: this.form.competitor_sku || null,
        competitor_product_name: this.form.competitor_product_name.trim(),
        competitor_original_price: parseFloat(this.form.competitor_original_price),
        competitor_sale_price: parseFloat(this.form.competitor_sale_price),
        competitor_stock: this.form.competitor_stock,
        matched_by: 'Humano'
      }

      this.store({
        url: '/api/pricescrapings/manual_entry/',
        params: payload
      }).then(() => {
        this.$notification.success(
          'Registro guardado exitosamente.',
          { timer: 5, showCloseIcn: true }
        )
        this.loadingSave = false
        // Limpiar producto y formulario, mantener competidor
        this.onClearProduct()
      }).catch((error) => {
        this.loadingSave = false
        if (error && error.status === 400 && error.data) {
          this.errors = error.data.errors || error.data
          this.$notification.error(
            `Error de validación: ${error.data.message || 'Revise los campos'}`,
            { timer: 8, showCloseIcn: true }
          )
        } else {
          this.$notification.error(
            'Ocurrió un error al guardar el registro. Intente nuevamente.',
            { timer: 8, showCloseIcn: true }
          )
        }
      })
    }
  }
}
</script>

<style scoped>
.manual-survey .opacity-50 {
  opacity: 0.5;
}
.manual-survey .pointer-events-none {
  pointer-events: none;
}

@media (min-width: 992px) {
  .px-lg-15 {
    padding-left: 3rem !important;
    padding-right: 3rem !important;
  }
}
</style>
